import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../assets/theme';
import AiFeedbackScoreboard from '../../components/_organisms/AiFeedbackScoreboard/AiFeedbackScoreboard';

type Scenario =
  | 'anticipating'
  | 'trickle'
  | 'scoring-partial'
  | 'done-fixed'
  | 'done-tally'
  | 'no-responses';

const SAMPLE_SCORES_FIXED = [5, 4, 4, 3, 5, 2, 5, 4, 3, 5, 1, 4];
const SAMPLE_SCORES_TALLY = [12, 8, 14, 6, 11, 9, 13, 7, 10, 12, 5, 14];

type ScenarioConfig = {
  label: string;
  description: string;
  scores: number[];
  maxScore: number;
  totalStudents: number;
  isScoring: boolean;
  trickle?: boolean;
};

const SCENARIOS: Record<Scenario, ScenarioConfig> = {
  anticipating: {
    label: 'Anticipating (loader)',
    description:
      'isScoring=true, scores=[]. Loader stays visible indefinitely — verifies the data-driven gate.',
    scores: [],
    maxScore: 5,
    totalStudents: 12,
    isScoring: true,
  },
  trickle: {
    label: 'Trickle in after 5 s',
    description:
      'Starts empty with isScoring=true; after 5 s, all scores tick in at once. Loader should vanish exactly when scores arrive.',
    scores: SAMPLE_SCORES_FIXED,
    maxScore: 5,
    totalStudents: SAMPLE_SCORES_FIXED.length,
    isScoring: true,
    trickle: true,
  },
  'scoring-partial': {
    label: 'Mid-scoring (partial)',
    description:
      'Half the scores have arrived, isScoring=true. Verifies the larger "AI scoring responses · X of Y" header.',
    scores: SAMPLE_SCORES_FIXED.slice(0, 6),
    maxScore: 5,
    totalStudents: SAMPLE_SCORES_FIXED.length,
    isScoring: true,
  },
  'done-fixed': {
    label: 'Done · fixed max (5 pts)',
    description:
      'All scores arrived, isScoring=false. Verifies the larger "all N responses scored" header.',
    scores: SAMPLE_SCORES_FIXED,
    maxScore: 5,
    totalStudents: SAMPLE_SCORES_FIXED.length,
    isScoring: false,
  },
  'done-tally': {
    label: 'Done · tally mode',
    description:
      'maxScore = -1 (unlimited), sparse distribution. Header should still scale.',
    scores: SAMPLE_SCORES_TALLY,
    maxScore: -1,
    totalStudents: SAMPLE_SCORES_TALLY.length,
    isScoring: false,
  },
  'no-responses': {
    label: 'No responses · isScoring=false',
    description:
      'Empty slide edge case. Loader should NOT show because isScoring=false (the safety gate).',
    scores: [],
    maxScore: 5,
    totalStudents: 0,
    isScoring: false,
  },
};

const DevScoreboardFixture = () => {
  const [scenario, setScenario] = useState<Scenario>('anticipating');
  const [tick, setTick] = useState(0); // force-remount the scoreboard
  const [trickleArrived, setTrickleArrived] = useState(false);
  const trickleTimerRef = useRef<number | null>(null);

  const config = SCENARIOS[scenario];

  // Manage the trickle scenario: scores start empty, then arrive after 5 s.
  useEffect(() => {
    if (trickleTimerRef.current !== null) {
      window.clearTimeout(trickleTimerRef.current);
      trickleTimerRef.current = null;
    }
    setTrickleArrived(false);
    if (config.trickle) {
      trickleTimerRef.current = window.setTimeout(() => {
        setTrickleArrived(true);
      }, 5000);
    }
    return () => {
      if (trickleTimerRef.current !== null) {
        window.clearTimeout(trickleTimerRef.current);
      }
    };
  }, [scenario, tick, config.trickle]);

  const effectiveScores = config.trickle && !trickleArrived ? [] : config.scores;

  return (
    <>
      <Head>
        <title>Scoreboard fixture</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <AiFeedbackScoreboard
        key={`${scenario}-${tick}`}
        scores={effectiveScores}
        maxScore={config.maxScore}
        totalStudents={config.totalStudents}
        isScoring={config.isScoring}
        onSeeAllResponses={() => undefined}
      />

      <ControlPanel>
        <BackLink href="/dev/fixtures">← Fixtures</BackLink>
        <PanelTitle>Scoreboard fixture</PanelTitle>
        <PanelDescription>{config.description}</PanelDescription>
        <ScenarioList>
          {(Object.keys(SCENARIOS) as Scenario[]).map(key => (
            <ScenarioButton
              key={key}
              $active={key === scenario}
              onClick={() => setScenario(key)}
            >
              {SCENARIOS[key].label}
            </ScenarioButton>
          ))}
        </ScenarioList>
        <ReplayButton onClick={() => setTick(t => t + 1)}>Replay (remount)</ReplayButton>
      </ControlPanel>
    </>
  );
};

export default DevScoreboardFixture;

const ControlPanel = styled.aside`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 2147483647;
  width: 280px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.greyLight};
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: 'Nunito', sans-serif;
`;

const BackLink = styled(Link)`
  font-size: 12px;
  font-weight: 800;
  color: ${theme.colors.orangeDark};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const PanelTitle = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  color: ${theme.colors.black};
`;

const PanelDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${theme.colors.greyDark};
  font-weight: 600;
  line-height: 1.4;
`;

const ScenarioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ScenarioButton = styled.button<{ $active: boolean }>`
  text-align: left;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid
    ${({ $active }) => ($active ? theme.colors.orange : theme.colors.greyLight)};
  background: ${({ $active }) => ($active ? theme.colors.paleBeige : theme.colors.white)};
  color: ${theme.colors.black};
  font-weight: ${({ $active }) => ($active ? 900 : 700)};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: ${theme.colors.paleBeige};
    border-color: ${theme.colors.orange};
  }
`;

const ReplayButton = styled.button`
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid ${theme.colors.greyLight};
  background: ${theme.colors.background};
  color: ${theme.colors.black};
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.paleBeige};
  }
`;
